import {IDisposableObject} from "./disposable-object.interface";
import {IReactionDisposer, IReactionOptions, IReactionPublic, reaction as mobxReaction} from "mobx";
import {
    IGameEventSubscription
} from "../app-react/services/game-runner/interfaces/game-communication/game-communication-channel.interface";
import {CGPEventSubscription} from "../app-react/services/sdk/cgp-sdk.interface";
import {TimeIntervalName} from "../common/utils/time-interval-names";
import {Check, TimeSpan} from "@sparkware/app-utils";
import {namedSetInterval, namedSetTimeout} from "../common/utils/time-functions";
import {
    TimerSubscriptionRef
} from "../app-react/services/companies/game-session-behaviors/common/game-session-behavior-base";
import {DomEventSubscription} from "./event-listener-registration";

export class DisposableObject implements IDisposableObject {
    private _reactions: IReactionDisposer[] = [];
    addReaction<T, FireImmediately extends boolean = false>(expression: (r: IReactionPublic) => T, effect: (arg: T, prev: FireImmediately extends true ? T | undefined : T, r: IReactionPublic) => void, opts?: IReactionOptions<T, FireImmediately>): void {
        this._reactions.push(mobxReaction(expression, effect, opts));
    }

    private _gameEventsSubscription: IGameEventSubscription[] = [];
    protected addGameEventSubscription(subscription: IGameEventSubscription): IGameEventSubscription {
        this._gameEventsSubscription.push(subscription);

        return {
            unsubscribe: () =>  {
                const index = this._gameEventsSubscription.findIndex(s => s === subscription);
                if(index >= 0) {
                    this._gameEventsSubscription[index].unsubscribe();
                    this._gameEventsSubscription.splice(index, 1);
                }
            }
        }
    }

    private _sdkEventsSubscriptions: CGPEventSubscription[] = [];
    protected addSdkEventSubscription(subscription: CGPEventSubscription): void {
        this._sdkEventsSubscriptions.push(subscription);
    }

    private _intervals: number[] = [];
    protected addInterval(handler: () => void, interval: TimeSpan | number = 0, name?: TimeIntervalName): TimerSubscriptionRef {
        const ref = name ? namedSetInterval(name, handler, interval) : window.setInterval(handler, Check.isNumber(interval) ? interval : interval.totalMilliseconds);
        this._intervals.push(ref);
        return {
            clear: () => {
                clearInterval(ref);
                const index = this._intervals.findIndex(i => i === ref);
                if(index >= 0) {
                    this._intervals.splice(index, 1);
                }
            }
        }
    }

    private _timeouts: number[] = [];
    protected addTimeout(handler: () => void, interval: TimeSpan | number = 0, name?: TimeIntervalName): TimerSubscriptionRef {
        const ref = name ? namedSetTimeout(name, handler, interval) : window.setTimeout(handler, Check.isNumber(interval) ? interval : interval.totalMilliseconds);
        this._timeouts.push(ref);
        return {
            clear: () => {
                clearTimeout(ref);
                const index = this._timeouts.findIndex(t => t === ref);
                if(index >= 0) {
                    this._timeouts.splice(index, 1);
                }
            }
        }
    }

    private _domEventsSubscriptions: DomEventSubscription[] = [];
    protected addDomEventSubscription(subscription: DomEventSubscription): DomEventSubscription {
        this._domEventsSubscriptions.push(subscription);
        return subscription;
    }

    dispose(): void {
        this._reactions.forEach(r => r());
        this._reactions = [];
        this._gameEventsSubscription.forEach(s => s.unsubscribe());
        this._gameEventsSubscription = [];
        this._intervals.forEach(i => clearInterval(i));
        this._intervals = [];
        this._timeouts.forEach(t => clearTimeout(t));
        this._timeouts = [];
        this._sdkEventsSubscriptions.forEach(s => s.off());
        this._sdkEventsSubscriptions = [];
        this._domEventsSubscriptions.forEach(s => s.dispose());
        this._domEventsSubscriptions = [];

    }
}